import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { Mail, MessageCircle, ArrowLeft, Send, CheckCircle2, Loader2 } from "lucide-react";
import API from "../api/axiosConfig";
import { isNativeApp } from "../utils/platform";

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);

  const [channel, setChannel] = useState("email"); // "email" | "whatsapp"
  const [identifier, setIdentifier] = useState("");
  const [countryCode, setCountryCode] = useState("+57");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const captchaToken = isNativeApp ? undefined : recaptchaRef.current?.getValue();
    if (!isNativeApp && !captchaToken) {
      setError("Por favor completa el CAPTCHA.");
      return;
    }

    const fullIdentifier =
      channel === "whatsapp"
        ? `${countryCode}${identifier.replace(/\D/g, "")}`
        : identifier;

    try {
      setLoading(true);
      await API.post("/auth/forgot-password", {
        identifier: fullIdentifier,
        channel,
        captchaToken,
      });
      setSent(true);
    } catch (err) {
      const msg = err.response?.data?.message;
      setError(msg || "Ocurrió un error. Inténtalo de nuevo.");
      recaptchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 size={36} className="text-green-500" />
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">¡Solicitud enviada!</h1>
          <p className="text-sm text-gray-500 mb-6">
            {channel === "email"
              ? "Si el correo está registrado, recibirás un enlace para restablecer tu contraseña en los próximos minutos."
              : "Si el número está registrado, recibirás el enlace por WhatsApp en breve."}
          </p>
          <button
            onClick={() => navigate("/login")}
            className="w-full py-3 rounded-xl bg-gray-900 hover:bg-gray-800 text-white font-semibold transition"
          >
            Volver al inicio de sesión
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">

        {/* Header */}
        <button
          onClick={() => navigate("/login")}
          className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-600 mb-6 transition"
        >
          <ArrowLeft size={15} /> Volver al login
        </button>

        <h1 className="text-2xl font-bold text-gray-900 mb-1">Recuperar contraseña</h1>
        <p className="text-sm text-gray-500 mb-6">
          Elige cómo quieres recibir el enlace de recuperación.
        </p>

        {/* Channel selector */}
        <div className="flex rounded-xl bg-gray-100 p-1 mb-6">
          <button
            type="button"
            onClick={() => { setChannel("email"); setIdentifier(""); setError(""); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition ${
              channel === "email"
                ? "bg-white shadow text-gray-900"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Mail size={15} /> Correo electrónico
          </button>
          <button
            type="button"
            onClick={() => { setChannel("whatsapp"); setIdentifier(""); setError(""); }}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition ${
              channel === "whatsapp"
                ? "bg-white shadow text-gray-900"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <MessageCircle size={15} /> WhatsApp
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">

          {channel === "email" ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Correo electrónico
              </label>
              <input
                type="email"
                placeholder="tu@correo.com"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 focus:border-transparent"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Número de WhatsApp
              </label>
              <div className="flex gap-2">
                <select
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="w-24 px-2 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 bg-white"
                >
                  <option value="+57">🇨🇴 +57</option>
                  <option value="+58">🇻🇪 +58</option>
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+52">🇲🇽 +52</option>
                  <option value="+34">🇪🇸 +34</option>
                  <option value="+54">🇦🇷 +54</option>
                  <option value="+56">🇨🇱 +56</option>
                  <option value="+51">🇵🇪 +51</option>
                  <option value="+593">🇪🇨 +593</option>
                </select>
                <input
                  type="tel"
                  placeholder="3001234567"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value.replace(/\D/g, ""))}
                  required
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-supernova-400 focus:border-transparent"
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Ingresa el número sin el código de país</p>
            </div>
          )}

          {/* reCAPTCHA */}
          {!isNativeApp && (
            <div className="flex justify-center pt-1">
              <ReCAPTCHA ref={recaptchaRef} sitekey={SITE_KEY} />
            </div>
          )}

          {error && (
            <p className="text-sm text-red-500 text-center">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl bg-supernova-400 hover:bg-supernova-500 text-black font-bold transition flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? (
              <><Loader2 size={16} className="animate-spin" /> Enviando...</>
            ) : (
              <><Send size={16} /> Enviar enlace</>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
